import { LogIn, Menu, ShoppingCart, X } from 'lucide-react';
import { SearchBar } from './SearchBar';
import { UserMenu } from '../UserMenu';
import { PreviewModeToggle } from './PreviewModeToggle';
import { ThemeColors } from '../../types';
import { useUser } from '../../hooks/useUser';
import { useGuestMode } from '../../hooks/useGuestMode';

interface HeaderActionsProps {
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isPreviewMode: boolean;
  onTogglePreviewMode: () => void;
  cartItemsCount: number;
  onCartMouseEnter: () => void;
  onCartMouseLeave: () => void;
  onShowPromos: () => void;
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  themeColors?: ThemeColors;
  showCart?: boolean;
}

export function HeaderActions({
  searchQuery,
  onSearchChange,
  isPreviewMode,
  onTogglePreviewMode,
  cartItemsCount,
  onCartMouseEnter,
  onCartMouseLeave,
  onShowPromos,
  isMenuOpen,
  onToggleMenu,
  themeColors,
  showCart = true,
}: HeaderActionsProps) {
  const { promoCodes } = useUser();
  const { isGuest, isAuthenticated } = useGuestMode();

  // Calculate promo code balance (PT-2219)
  // Only show if user has exactly ONE active DOLLAR-type promo with non-zero balance
  let promoCodeBalance: number | null = null;
  if (promoCodes && typeof promoCodes === 'object') {
    const promoCodesArray = Object.values(promoCodes);
    const dollarPromosWithBalance = promoCodesArray.filter(
      promo => promo.active && promo.promoType === 'DOLLAR' && promo.balance > 0
    );
    if (dollarPromosWithBalance.length === 1) {
      promoCodeBalance = dollarPromosWithBalance[0].balance;
    }
  }

  return (
    <div className="flex items-center space-x-4">
      {/* Search */}
      <div className="hidden sm:block">
        <SearchBar
          id="desktop-search"
          value={searchQuery}
          onChange={onSearchChange}
        />
      </div>

      {/* Preview Mode Toggle - Only show for authenticated users */}
      {isAuthenticated && (
        <PreviewModeToggle
          isPreviewMode={isPreviewMode}
          onToggle={onTogglePreviewMode}
        />
      )}

      {/* Available Balance - Only show if exactly one DOLLAR promo with balance */}
      {promoCodeBalance !== null && (
        <div
          className="hidden sm:inline-flex items-center gap-2 px-3.5 py-2.5 rounded-lg border"
          style={{
            background: '#e9f9f2',
            borderColor: '#bfe9d6'
          }}
          title="Your available promo code balance"
        >
          {/* Wallet Icon */}
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ flexShrink: 0 }}
            aria-hidden="true"
          >
            <path
              d="M3 7C3 5.89543 3.89543 5 5 5H19C20.1046 5 21 5.89543 21 7V17C21 18.1046 20.1046 19 19 19H5C3.89543 19 3 18.1046 3 17V7Z"
              stroke="#0f766e"
              strokeWidth="1.8"
            />
            <path
              d="M17 12H21"
              stroke="#0f766e"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>

          {/* Text */}
          <div className="flex flex-col" style={{ lineHeight: '1.1' }}>
            <span style={{ fontSize: '12px', color: '#0f766e', fontWeight: 500 }}>
              Available Balance
            </span>
            <span style={{ fontSize: '16px', color: '#065f46', fontWeight: 700 }}>
              ${promoCodeBalance.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* Cart Button */}
      {showCart && (
        <button
          onMouseEnter={onCartMouseEnter}
          onMouseLeave={onCartMouseLeave}
          className="relative p-2 hover:text-blue-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md"
          style={{ color: themeColors?.header_cart_background || '#6D6A69' }}
          aria-label={`Shopping cart${cartItemsCount > 0 ? ` with ${cartItemsCount} items` : ', empty'}`}
        >
          <ShoppingCart className="w-6 h-6" aria-hidden="true" />
          {cartItemsCount > 0 && (
            <span
              className="absolute -top-1 -right-1 text-xs font-medium min-w-[20px] h-5 rounded-full flex items-center justify-center px-1"
              style={{
                backgroundColor: themeColors?.header_cart_background || '#6D6A69',
                color: themeColors?.header_cart_text_color || 'white'
              }}
              aria-hidden="true"
            >
              {cartItemsCount}
            </span>
          )}
        </button>
      )}

      {/* User Menu / Login Button */}
      {isAuthenticated && (
        <UserMenu
          onShowPromos={onShowPromos}
        />
      )}
      {isGuest && (
        <a
          href="/login"
          className="flex items-center gap-2 px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          style={{
            backgroundColor: themeColors?.header_cart_background || '#6D6A69',
            color: themeColors?.header_cart_text_color || '#ffffff'
          }}
          aria-label="Login"
        >
          <LogIn className="w-5 h-5" aria-hidden="true" />
          <span className="text-sm font-medium">Login</span>
        </a>
      )}

      {/* Mobile Menu Button */}
      <button
        onClick={onToggleMenu}
        className="md:hidden p-2 text-gray-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md"
        aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isMenuOpen}
        aria-controls="mobile-menu"
      >
        {isMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
      </button>
    </div>
  );
}
